// @ts-nocheck
/* eslint-disable react/prop-types */
/**
 * SolyticsFlow — Main Wrapper Component
 *
 * A general-purpose wrapper around @xyflow/react.
 * Provides built-in custom node/edge types, group auto-adoption,
 * and theming out of the box.
 *
 * Developers pass nodes, edges, and handlers — SolyticsFlow handles
 * the ReactFlowProvider, default node/edge types, minimap, controls,
 * and background grid internally.
 *
 * Usage:
 *   <SolyticsFlow
 *     nodes={nodes}
 *     edges={edges}
 *     onNodesChange={onNodesChange}
 *     onEdgesChange={onEdgesChange}
 *     onConnect={onConnect}
 *   />
 */
import React, { memo, useMemo, useCallback } from 'react';
import {
  ReactFlow,
  ReactFlowProvider,
  MiniMap,
  Controls,
  Background,
  BackgroundVariant,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { useTheme } from '@mui/material/styles';

import { CustomNodeTypes } from './Internals/SolyticsFlow/Nodes';
import { CustomEdgeTypes } from './Internals/SolyticsFlow/Edges';
import { DEFAULT_EDGE_OPTIONS } from './Internals/SolyticsFlow/constants';
import useGroupNodeAdoption, {
  adoptNodeOnDrop,
  ensureChildAfterParent,
  clampChildPosition,
} from './Internals/SolyticsFlow/shared/useGroupNodeAdoption';
import SolyticsBox from './SolyticsBox';

// ── Inner component (must be inside ReactFlowProvider) ──────────────────
const SolyticsFlowInner = memo(
  ({
    nodes = [],
    edges = [],
    onNodesChange,
    onEdgesChange,
    onConnect,
    onReconnect,
    onReconnectEnd,
    onDrop,
    onDragOver,
    onNodeClick,
    onEdgeClick,
    onNodeContextMenu,
    onNodeDragStop: externalOnNodeDragStop,
    onInit,
    nodeTypes: externalNodeTypes,
    edgeTypes: externalEdgeTypes,
    readonly = false,
    showMiniMap = true,
    showControls = true,
    showBackground = true,
    defaultEdgeOptions: externalEdgeOptions,
    fitView = false,
    // Group adoption callbacks
    setNodes,
    onNodeAdopted,
    onNodeOrphaned,
    children,
  }) => {
    const theme = useTheme();

    const processedNodes = useMemo(() => {
      const mapped = nodes.map((node) => {
        if (node.type === 'group' && node.zIndex === undefined) {
          // Mutate to preserve referential equality, avoiding infinite ReactFlow render loops
          // eslint-disable-next-line no-param-reassign
          node.zIndex = -1;
        }

        if (node.parentId && node.extent !== 'parent') {
          // eslint-disable-next-line no-param-reassign
          node.extent = 'parent';
        }

        if (!node.parentId && node.extent === 'parent') {
          // eslint-disable-next-line no-param-reassign
          node.extent = undefined;
        }

        return node;
      });
      return ensureChildAfterParent(mapped);
    }, [nodes]);

    // Merge external node/edge types with internal defaults
    const mergedNodeTypes = useMemo(
      () => ({ ...CustomNodeTypes, ...externalNodeTypes }),
      [externalNodeTypes]
    );

    const mergedEdgeTypes = useMemo(
      () => ({ ...CustomEdgeTypes, ...externalEdgeTypes }),
      [externalEdgeTypes]
    );

    const mergedEdgeOptions = useMemo(
      () => ({ ...DEFAULT_EDGE_OPTIONS, ...externalEdgeOptions }),
      [externalEdgeOptions]
    );

    const defaultConnectionLineStyle = useMemo(
      () => ({
        stroke: theme.palette.primary.main,
        strokeWidth: 2,
      }),
      [theme.palette.primary.main]
    );

    // Group adoption hook
    const hasGroups = useMemo(
      () => nodes.some((n) => n.type === 'group'),
      [nodes]
    );

    const handleGroupAdoption = useGroupNodeAdoption(
      nodes,
      setNodes || (() => {}),
      onNodeAdopted,
      onNodeOrphaned
    );

    const handleNodeDragStop = useCallback(
      (event, node) => {
        // Run group adoption if setNodes is provided and groups exist
        if (hasGroups && setNodes) {
          handleGroupAdoption(event, node);
        }
        // Forward to external handler
        externalOnNodeDragStop?.(event, node);
      },
      [hasGroups, setNodes, handleGroupAdoption, externalOnNodeDragStop]
    );

    // Clamp child nodes inside their parent group during drag
    const handleNodeDrag = useCallback(
      (_event, draggedNode) => {
        if (!draggedNode.parentId || !setNodes) return;

        const parentNode = nodes.find((n) => n.id === draggedNode.parentId);
        if (!parentNode) return;

        const clamped = clampChildPosition(draggedNode, parentNode);
        if (
          clamped.x !== draggedNode.position.x ||
          clamped.y !== draggedNode.position.y
        ) {
          setNodes((prev) =>
            prev.map((n) =>
              n.id === draggedNode.id ? { ...n, position: clamped } : n
            )
          );
        }
      },
      [nodes, setNodes]
    );

    // Wrap onDrop to handle adoption for sidebar DnD drops
    const handleDrop = useCallback(
      (event) => {
        if (!onDrop || !setNodes) {
          onDrop?.(event);
          return;
        }

        // Snapshot current node IDs before the external drop adds new ones
        const existingIdsBefore = new Set(nodes.map((n) => n.id));

        // Let the external handler run first (it adds the node to state)
        onDrop(event);

        // If groups exist, check only the newly-added node(s)
        if (hasGroups) {
          setNodes((prev) => {
            const groupNodes = prev.filter((n) => n.type === 'group');
            if (groupNodes.length === 0) return prev;

            const updated = prev.map((n) => {
              // Only process nodes that were just added (not existing before drop)
              if (existingIdsBefore.has(n.id)) return n;
              // Skip groups
              if (n.type === 'group') return n;

              return adoptNodeOnDrop(n.position, n, prev);
            });

            return ensureChildAfterParent(updated);
          });
        }
      },
      [onDrop, setNodes, hasGroups, nodes]
    );

    return (
      <SolyticsBox
        sx={{
          width: '100%',
          height: '100%',
          // Remove ReactFlow's default group styling to prevent double borders
          '& .react-flow__node-group': {
            backgroundColor: 'transparent',
            border: 'none',
            boxShadow: 'none',
            borderRadius: 0,
            '&.selected, &:focus, &:focus-visible': {
              outline: 'none',
              boxShadow: 'none',
            },
          },
        }}
      >
        <ReactFlow
          nodes={processedNodes}
          edges={edges}
          onNodesChange={readonly ? undefined : onNodesChange}
          onEdgesChange={readonly ? undefined : onEdgesChange}
          onConnect={readonly ? undefined : onConnect}
          onReconnect={readonly ? undefined : onReconnect}
          onReconnectEnd={readonly ? undefined : onReconnectEnd}
          onDrop={readonly ? undefined : handleDrop}
          onDragOver={readonly ? undefined : onDragOver}
          onNodeClick={onNodeClick}
          onEdgeClick={onEdgeClick}
          onNodeContextMenu={readonly ? undefined : onNodeContextMenu}
          onNodeDrag={readonly ? undefined : handleNodeDrag}
          onNodeDragStop={readonly ? undefined : handleNodeDragStop}
          onInit={onInit}
          nodeTypes={mergedNodeTypes}
          edgeTypes={mergedEdgeTypes}
          defaultEdgeOptions={mergedEdgeOptions}
          connectionLineStyle={defaultConnectionLineStyle}
          nodesDraggable={!readonly}
          nodesConnectable={!readonly}
          edgesReconnectable={!readonly}
          elementsSelectable={!readonly}
          fitView={fitView}
        >
          {showBackground && (
            <Background
              variant={BackgroundVariant.Dots}
              gap={16}
              size={1}
              color={
                theme.palette.solyticsFlow?.canvas?.dotColor ||
                theme.palette.grey?.[60]
              }
            />
          )}
          {showControls && <Controls />}
          {showMiniMap && (
            <MiniMap
              nodeStrokeWidth={3}
              zoomable
              pannable
              nodeColor={() => theme.palette.background.paper}
              nodeStrokeColor={() => theme.palette.divider}
              style={{
                border: `1px solid ${
                  theme.palette.solyticsFlow?.canvas?.minimapBorder ||
                  theme.palette.grey?.[60]
                }`,
                borderRadius: '4px',
              }}
            />
          )}
          {children}
        </ReactFlow>
      </SolyticsBox>
    );
  }
);
SolyticsFlowInner.displayName = 'SolyticsFlowInner';

// ── Outer wrapper with ReactFlowProvider ────────────────────────────────
const SolyticsFlow = memo(
  ({
    nodes,
    edges,
    onNodesChange,
    onEdgesChange,
    onConnect,
    onReconnect,
    onReconnectEnd,
    onDrop,
    onDragOver,
    onNodeClick,
    onEdgeClick,
    onNodeContextMenu,
    onNodeDragStop,
    onInit,
    nodeTypes,
    edgeTypes,
    readonly,
    showMiniMap,
    showControls,
    showBackground,
    defaultEdgeOptions,
    fitView,
    setNodes,
    onNodeAdopted,
    onNodeOrphaned,
    children,
  }) => {
    return (
      <ReactFlowProvider>
        <SolyticsFlowInner
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onReconnect={onReconnect}
          onReconnectEnd={onReconnectEnd}
          onDrop={onDrop}
          onDragOver={onDragOver}
          onNodeClick={onNodeClick}
          onEdgeClick={onEdgeClick}
          onNodeContextMenu={onNodeContextMenu}
          onNodeDragStop={onNodeDragStop}
          onInit={onInit}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          readonly={readonly}
          showMiniMap={showMiniMap}
          showControls={showControls}
          showBackground={showBackground}
          defaultEdgeOptions={defaultEdgeOptions}
          fitView={fitView}
          setNodes={setNodes}
          onNodeAdopted={onNodeAdopted}
          onNodeOrphaned={onNodeOrphaned}
        >
          {children}
        </SolyticsFlowInner>
      </ReactFlowProvider>
    );
  }
);
SolyticsFlow.displayName = 'SolyticsFlow';

export default SolyticsFlow;
